import { Clock, Info, Plus } from "lucide-react";
import React, { useState, useEffect, useContext, useMemo } from "react";
import RenderTooltip from "../../../Shared/tooltip/tooltip";
import AddBalanceDialog from "./AddBalanceDialog";
import AlertBalanceDialog from "./AlertBalanceDialog";
import { callApi } from "../../../api/fetch";
import Loading from "../../../Shared/loading/loading";
import { ACCENTCOLOR } from "../../../Shared/colors";
import { environmentContext } from "../../../context/environmentContext";
import { PlanType, WalletEnvironment } from "../../../constants/orderTypes";
import { testIdProps } from "../../../utils/testIdProps";
import { unwrapOrgSummary } from "../../../utils/dashboardMappers";

type WalletDetailsType = {
  orgPlanType: string | null;
};

const WalletDetails = ({ orgPlanType }: WalletDetailsType) => {
  const [showAddBalanceDialog, setShowAddBalanceDialog] = useState(false);
  const [showAlertDialog, setShowAlertDialog] = useState(false);
  const [walletData, setWalletData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [orgDetails, setOrgDetails] = useState<any>();

  const environmentCtx = useContext(environmentContext);
  const { environment } = environmentCtx;

  const orgId = localStorage.getItem("organisation_id");

  const fetchWalletData = async () => {
    try {
      setLoading(true);
      setError(null);

      setWalletData(
        unwrapOrgSummary(
          await callApi(`/organisations/${orgId}/summary`, {
            method: "GET",
          })
        ).wallets
      );
    } catch (error: any) {
      setError(error?.message || "Failed to fetch wallet data");
      console.error("Wallet API Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const showAlertBalance = useMemo(() => {
    if (orgPlanType?.toUpperCase() === PlanType.PREPAID) {
      if (environment.toLowerCase() === "production") {
        return true;
      }

      return false;
    } else {
      return false;
    }
  }, [environment, orgPlanType]);

  const showAddBalanceButton = useMemo(() => {
    if (orgDetails?.country_code?.toUpperCase() != "IN") {
      return false;
    } else if (orgPlanType?.toUpperCase() === PlanType.PREPAID) {
      return true;
    } else {
      if (environment.toUpperCase() === WalletEnvironment.SANDBOX) {
        return true;
      } else {
        return false;
      }
    }
  }, [environment, orgPlanType, orgDetails]);

  useEffect(() => {
    async function getOrgDetails() {
      try {
        const data = await callApi(`/organisation`, {
          method: "GET",
        });
        setOrgDetails(data);
      } catch (err) {
        console.error("Failed to fetch org details:", err);
      }
    }

    if (orgId) {
      getOrgDetails();
    }
  }, [orgId]);

  useEffect(() => {
    if (orgId) {
      fetchWalletData();
    } else {
      setLoading(false);
    }
  }, [orgId]);

  const handleSetAlert = () => {
    setShowAlertDialog(true);
  };

  const handleAddBalance = () => {
    setShowAddBalanceDialog(true);
  };

  const handleAddBalanceSuccess = () => {
    setShowAddBalanceDialog(false);
    fetchWalletData();
  };

  const handleSetAlertSuccess = () => {
    setShowAlertDialog(false);
    fetchWalletData();
  };

  if (loading) {
    return (
      <div className="p-4">
        <div
          className="bg-white rounded-4 shadow-sm d-flex align-items-center"
          style={{ height: "85px" }}
        >
          <div className="d-flex align-items-center justify-content-center w-100">
            <Loading loadingColor={ACCENTCOLOR} />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-white rounded-4 shadow-sm p-4">
          <div className="text-center text-danger">Error: {error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div
        className="bg-white rounded-4 shadow-sm d-flex align-items-center"
        style={{ minHeight: "100px" }}
      >
        {!walletData?.length ? (
          <div className="text-center w-100 py-3">
            <div className="d-flex flex-column align-items-center gap-2">
              <div className="fw-medium text-danger">No Wallets Found</div>

              <div className="text-muted" style={{ fontSize: "12px" }}>
                Contact <span className="text-primary">support@zoop.one</span>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div
              className="d-flex justify-content-evenly align-items-center"
              style={{ width: "80%" }}
            >
              {walletData
                .filter((wallet) => wallet?.environment === environment)
                .sort((a, b) => {
                  const walletOrder = ["primary", "estamp", "challan"];
                  const indexA = walletOrder.indexOf(a.wallets_type);
                  const indexB = walletOrder.indexOf(b.wallets_type);
                  return indexA - indexB;
                })
                .map((wallet) => {
                  const walletAmount =
                    environment?.toLowerCase() === "production" &&
                    orgPlanType?.toUpperCase() === PlanType.POSTPAID &&
                    wallet?.wallets_type?.toLowerCase() === "primary"
                      ? "NA"
                      : `₹ ${wallet?.wallets_balance_with_gst?.toLocaleString() || "0"}`;

                  return (
                    <div
                      key={wallet.wallets_id}
                      className={`border-end`}
                      style={{ flex: 1 }}
                    >
                      <WalletCard
                        title={wallet?.wallets_type}
                        amount={walletAmount}
                        infoMsg={`Alert Balance: ₹${(wallet?.alert_balance || 0).toLocaleString()}`}
                        environment={environment}
                        orgPlanType={orgPlanType}
                      />
                    </div>
                  );
                })}
            </div>

            <div
              className="d-flex align-items-center gap-3 justify-content-center p-3"
              style={{ width: "20%" }}
            >
              {showAlertBalance ? (
                <div className="d-flex flex-column align-items-center">
                  <button
                    {...testIdProps("new-billing-set-alert")}
                    className="btn d-flex align-items-center justify-content-center border-0"
                    onClick={handleSetAlert}
                    style={{
                      backgroundColor: "#E8E8E8",
                      width: "48px",
                      height: "48px",
                      borderRadius: "8px",
                    }}
                  >
                    <Clock size={18} />
                  </button>

                  <div className="text-xs text-muted mt-1">Set Alert</div>
                </div>
              ) : (
                <></>
              )}

              {showAddBalanceButton ? (
                <div className="d-flex flex-column align-items-center">
                  <button
                    {...testIdProps("new-billing-add-balance")}
                    className="btn btn-dark d-flex align-items-center justify-content-center border-0"
                    onClick={handleAddBalance}
                    style={{
                      width: "48px",
                      height: "48px",
                      borderRadius: "8px",
                    }}
                  >
                    <Plus size={18} className="text-white" />
                  </button>

                  <div className="text-xs text-muted mt-1">Add Balance</div>
                </div>
              ) : (
                <></>
              )}
            </div>
          </>
        )}
      </div>

      <AddBalanceDialog
        show={showAddBalanceDialog}
        onClose={() => setShowAddBalanceDialog(false)}
        walletDetails={walletData.filter(
          (wallet) => wallet?.environment === environment
        )}
        onAddBalance={handleAddBalanceSuccess}
      />

      <AlertBalanceDialog
        show={showAlertDialog}
        onClose={() => setShowAlertDialog(false)}
        walletDetails={walletData.filter(
          (wallet) => wallet?.environment === environment
        )}
        onSetAlert={handleSetAlertSuccess}
      />
    </div>
  );
};

export default WalletDetails;

const WalletCard = ({
  title,
  amount,
  infoMsg,
  environment,
  orgPlanType,
}: {
  title: string;
  amount: string;
  infoMsg: string;
  environment: string;
  orgPlanType: string | null;
}) => {
  const formattedTitle =
    title === "primary"
      ? `Primary Wallet ${environment === "sandbox" ? "(Test)" : ""}`
      : title === "estamp"
        ? `E-stamp Wallet ${environment === "sandbox" ? "(Test)" : ""}`
        : title === "challan"
          ? `RC Challan Wallet ${environment === "sandbox" ? "(Test)" : ""}`
          : title || "Unknown Wallet";

  return (
    <div className="text-start px-5">
      <div className="d-flex align-items-center gap-2">
        <span className="text-secondary" style={{ fontSize: "12px" }}>
          {formattedTitle}
        </span>

        {environment?.toLowerCase() === "production" &&
        orgPlanType?.toUpperCase() === PlanType.PREPAID ? (
          <RenderTooltip placement="top" title={infoMsg}>
            <Info
              size={14}
              className="text-secondary cursor-pointer"
              style={{ marginTop: "-6px" }}
            />
          </RenderTooltip>
        ) : (
          <div></div>
        )}
      </div>

      <div className="fw-medium fs-6">{amount}</div>
    </div>
  );
};
